import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Loader2, Ticket } from "lucide-react";

const blank = { name: "", price: 200, total_tickets: 50, max_per_person: 2, available: true };

export default function ActivitiesTab() {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState(blank);
  const [saving, setSaving] = useState(false);
  const [deleteAct, setDeleteAct] = useState(null);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/activities/all"); setActivities(data); }
    catch (e) { toast.error("Failed to load activities"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setFormData(blank); setEditing("new"); };
  const openEdit = (a) => { setFormData({ ...blank, ...a }); setEditing(a); };
  const setF = (k, v) => setFormData((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!formData.name.trim()) { toast.error("Activity name is required"); return; }
    setSaving(true);
    try {
      const payload = {
        name: formData.name, price: Number(formData.price || 0),
        total_tickets: Number(formData.total_tickets || 0),
        max_per_person: Number(formData.max_per_person || 1),
        available: !!formData.available,
      };
      if (editing === "new") await api.post("/activities", payload);
      else await api.put(`/activities/${editing.id}`, payload);
      toast.success(editing === "new" ? "Activity created" : "Activity updated");
      setEditing(null); load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Failed to save");
    } finally { setSaving(false); }
  };

  const toggle = async (a) => {
    try {
      await api.put(`/activities/${a.id}`, { name: a.name, price: a.price, total_tickets: a.total_tickets, max_per_person: a.max_per_person, available: !a.available });
      setActivities((l) => l.map((x) => (x.id === a.id ? { ...x, available: !a.available } : x)));
    } catch (e) { toast.error("Failed to update"); }
  };

  const doDelete = async () => {
    try { await api.delete(`/activities/${deleteAct.id}`); toast.success("Activity deleted"); setDeleteAct(null); load(); }
    catch (e) { toast.error("Failed to delete"); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-[#A1A1AA]">Create paid activities with limited tickets. Tickets count as sold only after successful payment.</p>
        <Button data-testid="activity-new" onClick={openNew} className="bg-[#FF4500] hover:bg-[#FF5722]"><Plus className="h-4 w-4 mr-1" /> New Activity</Button>
      </div>

      <div className="rounded-xl border border-border overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="border-border hover:bg-transparent">
              <TableHead>Activity</TableHead>
              <TableHead>Price</TableHead>
              <TableHead>Tickets (sold / total)</TableHead>
              <TableHead>Max / person</TableHead>
              <TableHead>Available</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && <TableRow><TableCell colSpan={6} className="text-center py-8 text-[#71717A]"><Loader2 className="h-5 w-5 animate-spin inline" /></TableCell></TableRow>}
            {!loading && activities.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-8 text-[#71717A]">No activities yet.</TableCell></TableRow>}
            {!loading && activities.map((a) => (
              <TableRow key={a.id} className="border-border" data-testid={`activity-row-${a.id}`}>
                <TableCell className="font-semibold"><span className="inline-flex items-center gap-1.5"><Ticket className="h-3.5 w-3.5 text-[#FF4500]" />{a.name}</span></TableCell>
                <TableCell>₹{a.price}</TableCell>
                <TableCell className="text-[#A1A1AA]">{a.sold} / {a.total_tickets > 0 ? a.total_tickets : "∞"}{a.sold_out && <Badge className="ml-2 bg-red-500/20 text-red-300 border-0">Sold Out</Badge>}</TableCell>
                <TableCell className="text-[#A1A1AA]">{a.max_per_person}</TableCell>
                <TableCell><Switch data-testid={`activity-toggle-${a.id}`} checked={!!a.available} onCheckedChange={() => toggle(a)} /></TableCell>
                <TableCell>
                  <div className="flex justify-end gap-1">
                    <Button data-testid={`activity-edit-${a.id}`} onClick={() => openEdit(a)} variant="ghost" size="icon" className="h-8 w-8 hover:bg-white/10"><Pencil className="h-4 w-4" /></Button>
                    <Button data-testid={`activity-delete-${a.id}`} onClick={() => setDeleteAct(a)} variant="ghost" size="icon" className="h-8 w-8 hover:bg-red-500/15 text-red-400"><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-[#121212] border-border max-w-md">
          <DialogHeader><DialogTitle>{editing === "new" ? "Create Activity" : "Edit Activity"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Field label="Activity Name *"><Input data-testid="activity-form-name" value={formData.name} onChange={(e) => setF("name", e.target.value)} placeholder="Slow Bike Race" /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Price (₹) *"><Input data-testid="activity-form-price" type="number" min={0} value={formData.price} onChange={(e) => setF("price", e.target.value)} /></Field>
              <Field label="Total Tickets (0 = unlimited)"><Input data-testid="activity-form-total" type="number" min={0} value={formData.total_tickets} onChange={(e) => setF("total_tickets", e.target.value)} /></Field>
            </div>
            <Field label="Max Tickets Per Person"><Input data-testid="activity-form-maxper" type="number" min={1} value={formData.max_per_person} onChange={(e) => setF("max_per_person", e.target.value)} /></Field>
            <div className="flex items-center gap-3 pt-1">
              <Switch data-testid="activity-form-available" checked={!!formData.available} onCheckedChange={(v) => setF("available", v)} />
              <Label className="text-sm">Available</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} className="border-border">Cancel</Button>
            <Button data-testid="activity-form-save" onClick={save} disabled={saving} className="bg-[#FF4500] hover:bg-[#FF5722]">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteAct} onOpenChange={(o) => !o && setDeleteAct(null)}>
        <AlertDialogContent className="bg-[#121212] border-border">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {deleteAct?.name}?</AlertDialogTitle>
            <AlertDialogDescription>This cannot be undone. Past purchases keep their recorded tickets.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-border">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="activity-delete-confirm" onClick={doDelete} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs uppercase tracking-wide text-[#A1A1AA]">{label}</Label>
      {children}
    </div>
  );
}
